import { useEffect, useState } from "react";
import axios from "axios";

// Baza API: în producție backend-ul de pe Render; local se poate suprascrie cu
// REACT_APP_API_BASE=http://localhost:5050
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://esthetiquebasilixbackend.onrender.com";

export type PriceItem = {
  _id?: string;
  serviceId: string; // id-ul cardului de pe site, ex. "lifting-hifu"
  group: string; // numele serviciului (afișare admin)
  label: string; // eticheta liniei, ex. "Front"
  basePrice: number | null; // preț normal numeric
  priceText: string; // preț text liber (ex. game "50 - 100")
  promoPrice: number | null; // preț redus numeric
  promoText: string; // preț redus text liber
  promoActive: boolean;
  note: string; // ex. "(45min)"
  order: number;
};

export type OfferConfig = {
  visible: boolean;
  chipLabel: string;
  text: string;
  imageUrl: string;
  ctaLabel: string;
};

export type ServiceLabel = { serviceId: string; promoLabel: string };

export type PreturiConfig = {
  offer: OfferConfig | null;
  prices: PriceItem[];
  serviceLabels?: ServiceLabel[];
};

// Cache la nivel de modul: un singur request /preturi partajat de toate
// componentele (Cart, Offers) — evită dublarea apelului.
let cache: Promise<PreturiConfig> | null = null;

export function fetchPreturi(): Promise<PreturiConfig> {
  if (!cache) {
    cache = axios
      .get(`${API_BASE}/preturi`, { timeout: 12000 })
      .then((r) => r.data as PreturiConfig)
      .catch((err) => {
        cache = null; // permite reîncercarea la următorul mount
        throw err;
      });
  }
  return cache;
}

// Hook: întoarce { data, error }. Componentele fac fallback la valorile
// hardcodate când data e null sau error e true, ca site-ul să nu se strice.
export function usePreturi() {
  const [data, setData] = useState<PreturiConfig | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchPreturi()
      .then((d) => alive && setData(d))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);
  return { data, error };
}

// Rândurile de preț ale unui serviciu (după serviceId), sortate după `order`.
export function pricesForService(
  config: PreturiConfig | null,
  serviceId: string
): PriceItem[] {
  if (!config || !serviceId) return [];
  return config.prices
    .filter((p) => p.serviceId === serviceId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

// Eticheta promo (text roșu) a unui serviciu, dacă e setată din admin.
export function promoLabelFor(
  config: PreturiConfig | null,
  serviceId: string
): string {
  if (!config || !serviceId || !config.serviceLabels) return "";
  const m = config.serviceLabels.find((s) => s.serviceId === serviceId);
  return (m?.promoLabel || "").trim();
}

// Formatează un număr ca preț european: 112.5 -> "112,50 €", 135 -> "135 €".
export function formatPrice(n: number | null | undefined): string {
  if (n == null || isNaN(Number(n))) return "";
  const num = Number(n);
  const hasCents = Math.round(num * 100) % 100 !== 0;
  return (
    num.toLocaleString("fr-FR", {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

// Formatează un preț text liber, adăugând " €" dacă nu îl are deja.
export function formatPriceText(t: string | null | undefined): string {
  if (!t) return "";
  const s = String(t).trim();
  return /€/.test(s) ? s : s + " €";
}

// Reprezentarea afișabilă a bazei/promoului unui rând (numeric sau text).
export function baseDisplay(item: PriceItem): string {
  return item.basePrice != null
    ? formatPrice(item.basePrice)
    : formatPriceText(item.priceText);
}
export function promoDisplay(item: PriceItem): string {
  return item.promoPrice != null
    ? formatPrice(item.promoPrice)
    : formatPriceText(item.promoText);
}
export function hasPromo(item: PriceItem): boolean {
  return (
    !!item.promoActive && (item.promoPrice != null || !!(item.promoText || "").trim())
  );
}
